'use client'

import usePickerOpenState from '@trustoo/components/searchPicker/hooks/usePickerOpenState'
import usePickerSearch from '@trustoo/components/searchPicker/hooks/usePickerSearch'
import {
    DEFAULT_SETTINGS,
    SearchPickerOption,
    SearchPickerProvider,
    SearchPickerSettings,
} from '@trustoo/components/searchPicker/SearchPickerContext'
import { ComponentPropsWithoutRef, ReactNode, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'

const EMPTY_OPTIONS: SearchPickerOption[] = []

export interface SearchPickerRootProps extends ComponentPropsWithoutRef<'div'> {
    children: ReactNode
    selectedOption?: SearchPickerOption
    onSelectOption?: (option: SearchPickerOption) => void
    onResetOption?: () => void
    initialIsOpen?: boolean
    initialQuery?: string
    initialOptions?: SearchPickerOption[]
    fetchFunction?: (query: string) => Promise<SearchPickerOption[]>
    isOpen?: boolean
    onIsOpenChange?: (isOpen: boolean) => void
    settings?: Partial<SearchPickerSettings>
    isFullWidth?: boolean
}

/**
 * SearchPicker.Root - State Management & Context Provider
 *
 * **Role in Compound Structure:**
 * This is the top-level component that provides the context for the SearchPicker
 * compound component. It delegates logic to focused hooks and wires up the context.
 *
 * **Hooks used:**
 * - `usePickerOpenState` — controlled/uncontrolled open state
 * - `usePickerSearch` — query, debounced fetch, options, closePicker, query sync
 */
const SearchPickerRoot = ({
    className,
    children,
    initialIsOpen = false,
    initialQuery = '',
    initialOptions = EMPTY_OPTIONS,
    fetchFunction,
    selectedOption,
    onSelectOption,
    onResetOption,
    settings: settingsProp,
    isOpen: controlledIsOpen,
    onIsOpenChange,
    isFullWidth = true,
    style = {},
    ...rest
}: SearchPickerRootProps) => {
    const settings: SearchPickerSettings = {
        ...DEFAULT_SETTINGS,
        ...settingsProp,
    }

    const { isOpen, setIsOpen } = usePickerOpenState({
        initialIsOpen,
        controlledIsOpen,
        onIsOpenChange,
    })

    const { query, setQuery, search, options, setOptions, isLoading, hasNoResults, closePicker } =
        usePickerSearch({
            initialQuery,
            initialOptions,
            fetchFunction,
            selectedOption,
            onSelectOption,
            settings,
            setIsOpen,
        })

    const contentRef = useRef<HTMLDivElement>(null)
    const [maxHeight, setMaxHeight] = useState<number>(settings.maxHeight)

    const contextValue = {
        isOpen,
        setIsOpen,
        closePicker,
        contentRef,
        maxHeight,
        setMaxHeight,
        query,
        setQuery,
        search,
        options,
        setOptions,
        isLoading,
        hasNoResults,
        selectedOption,
        onSelectOption,
        onResetOption,
        settings,
    }

    return (
        <SearchPickerProvider value={contextValue}>
            <div
                className={twMerge('relative', isFullWidth ? 'w-full' : 'w-auto', className)}
                style={{ minHeight: settings.inputFieldHeight, ...style }}
                {...rest}>
                {children}
            </div>
        </SearchPickerProvider>
    )
}

export default SearchPickerRoot
